import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import type { ReportFragmentAction } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onReportSlideScrollAndContentChange from 'webapp/modifiers/on-report-slide-scroll-and-content-change';
import type { ReportFragmentActionData } from 'webapp/components/reports/editor-base';
import ReportsEditorSlideOverflowLineComponent from 'webapp/components/reports/editor/slide-overflow-line';
import ReportsSlideBottomInfoComponent from 'webapp/components/reports/slide-bottom-info';
import type { Args as BaseArgs } from 'webapp/components/reports/viewer/slide-wrapper';
import ReportsViewerSlideWrapperComponent from 'webapp/components/reports/viewer/slide-wrapper';

import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

interface Args extends BaseArgs {
	onFragmentChange: (
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	) => Promise<unknown>;
}

export default class ReportsEditorSlideWrapperComponent extends ReportsViewerSlideWrapperComponent<Args> {
	@tracked isEditorReady = true; // used to cleanly re-initialize the html editors
	@tracked currentModel: ReportSlide;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.currentModel = this.args.model;
	}

	@action
	checkUsercontentOverflow(usercontentNode: HTMLElement): void {
		if (!usercontentNode) {
			return;
		}

		const overflowLineNode = usercontentNode.nextElementSibling as HTMLElement;
		if (overflowLineNode?.classList.contains('overflow-line')) {
			const usercontentNodeHeight = usercontentNode.clientHeight,
				hasScrollbar = usercontentNode.scrollHeight > usercontentNodeHeight;

			if (hasScrollbar) {
				const top =
					usercontentNode.offsetTop + usercontentNodeHeight - usercontentNode.scrollTop;
				overflowLineNode.style.top = `${top}px`;
				overflowLineNode.classList.remove('hidden');
			} else {
				overflowLineNode.classList.add('hidden');
			}
		}
	}

	@action
	override onFragmentChange(
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	): Promise<unknown> {
		void super.onFragmentChange(fragment, action, data);
		this.checkUsercontentOverflow(this.usercontentNode!);
		return this.args.onFragmentChange(fragment, action, data);
	}

	@action
	onModelChange(): void {
		// an ACTUAL slide switch, not just saving current slide
		if (this.currentModel.id !== this.args.model.id) {
			this.isEditorReady = false;
			schedule('afterRender', () => {
				next(() => {
					this.currentModel = this.args.model;
					this.isEditorReady = true;
					schedule(
						'afterRender',
						this,
						this.checkUsercontentOverflow,
						this.usercontentNode!,
					);
				});
			});
		}
	}
	<template>
		{{#if @model.isTypeBoard}}
			<div class="report_slide report_slide--board">
				{{yield this.onFragmentChange}}
			</div>
		{{else}}

			<div
				class="report_slide bgTheme-{{@model.bgTheme}} colorTheme-{{@model.themeId}}"
				style={{@model.colorStyle}}
				{{didUpdate this.onModelChange @model}}
			>
				<div class="bg" style={{@model.bgStyle}}></div>

				<div class="content-inner">

					<div
						class="usercontent"
						{{didInsert this.setupUsercontentNode}}
						{{onReportSlideScrollAndContentChange this.checkUsercontentOverflow}}
					>
						{{yield this.onFragmentChange}}
					</div>

					<ReportsEditorSlideOverflowLineComponent @bgColor={{@model.shownColor}} />

					<ReportsSlideBottomInfoComponent
						@model={{@model}}
						@hideBranding={{this.hideBranding}}
						@canEdit={{true}}
					/>

				</div>
			</div>
		{{/if}}
	</template>
}
